import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/auth.store';
import { notificationsService, type NotificationItem } from '../services/notifications.service';

const NOTIFICATIONS_KEY = ['notifications'];

// Data hook for the notifications bell (list + unread count + mark-read).
// The real-time socket lives in useRealtimeNotifications (mounted once in the
// portal layout), which keeps this cache live — so this hook stays socket-free
// and can be called by multiple components without opening extra connections.
export function useNotifications() {
  const accessToken = useAuthStore((s) => s.accessToken);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const qc = useQueryClient();

  const { data } = useQuery({
    queryKey: NOTIFICATIONS_KEY,
    queryFn: () => notificationsService.list(20),
    enabled: isAuthenticated && !!accessToken,
    staleTime: 60_000,
  });

  const notifications: NotificationItem[] = data?.data ?? [];
  const unreadCount = notifications.filter((n) => n.notificationStatus === 'DELIVERED').length;

  const markAsRead = useCallback(
    async (id: string) => {
      await notificationsService.markAsRead(id);
      qc.setQueryData(NOTIFICATIONS_KEY, (prev: typeof data) => {
        if (!prev) return prev;
        return {
          ...prev,
          data: prev.data.map((n) =>
            n.id === id ? { ...n, notificationStatus: 'READ' as const } : n,
          ),
        };
      });
    },
    [qc],
  );

  return { notifications, unreadCount, markAsRead };
}
